// src/services/versionChecker.ts
// Service for checking application version and detecting updates

export interface VersionInfo {
  version: string;
  buildDate: string;
  commit: string;
  branch: string;
  environment: string;
  timestamp: number;
}

const VERSION_STORAGE_KEY = 'app_version';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch the current version from the server
 */
export const fetchCurrentVersion = async (): Promise<VersionInfo | null> => {
  try {
    // Add timestamp to prevent caching
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch version: ${response.status}`);
    }

    const versionData: VersionInfo = await response.json();
    return versionData;
  } catch (error) {
    console.error('Error fetching version:', error);
    return null;
  }
};

/**
 * Get the stored version from localStorage
 */
export const getStoredVersion = (): string | null => {
  return localStorage.getItem(VERSION_STORAGE_KEY);
};

/**
 * Store the current version in localStorage
 */
export const storeVersion = (version: string): void => {
  localStorage.setItem(VERSION_STORAGE_KEY, version);
};

/**
 * Check if a new version is available
 * Returns { hasUpdate: boolean, currentVersion: string, newVersion: string }
 */
export const checkForVersionUpdate = async (): Promise<{
  hasUpdate: boolean;
  currentVersion: string | null;
  newVersion: string | null;
  versionInfo: VersionInfo | null;
}> => {
  const storedVersion = getStoredVersion();
  const versionInfo = await fetchCurrentVersion();

  if (!versionInfo) {
    return {
      hasUpdate: false,
      currentVersion: storedVersion,
      newVersion: null,
      versionInfo: null
    };
  }

  const newVersion = versionInfo.version;

  // First time user - store version and don't show update
  if (!storedVersion) {
    storeVersion(newVersion);
    return {
      hasUpdate: false,
      currentVersion: null,
      newVersion,
      versionInfo
    };
  }

  // Check if versions are different
  const hasUpdate = storedVersion !== newVersion;

  if (hasUpdate) {
    console.log(`🔄 Version update detected: ${storedVersion} → ${newVersion}`);
  }

  return {
    hasUpdate,
    currentVersion: storedVersion,
    newVersion,
    versionInfo
  };
};

/**
 * Start periodic version checking
 * Returns a function to stop the interval
 */
export const startVersionPolling = (
  onUpdateDetected: (versionInfo: VersionInfo) => void,
  interval: number = VERSION_CHECK_INTERVAL
): (() => void) => {
  console.log('🔍 Starting version polling...');

  const checkVersion = async () => {
    const { hasUpdate, versionInfo } = await checkForVersionUpdate();
    if (hasUpdate && versionInfo) {
      console.log('🆕 New version available:', versionInfo);
      onUpdateDetected(versionInfo);
    }
  };

  // Check immediately
  checkVersion();

  // Then check periodically
  const intervalId = setInterval(checkVersion, interval);

  // Return cleanup function
  return () => {
    console.log('🛑 Stopping version polling');
    clearInterval(intervalId);
  };
};

/**
 * Apply the update by storing the new version and reloading
 */
export const applyVersionUpdate = (version: string): void => {
  storeVersion(version);
  console.log('✅ Applying update, reloading...');
  window.location.reload();
};

/**
 * Clear all caches and force a hard reload
 * This is more aggressive and clears service worker caches too
 */
export const clearAllCachesAndReload = async (): Promise<void> => {
  try {
    // 1. Clear all cache storage
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('✅ Cleared cache storage');
    }

    // 2. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      console.log('✅ Unregistered service workers');
    }

    // 3. Clear session storage (but keep localStorage for version tracking)
    sessionStorage.clear();
    console.log('✅ Cleared session storage');

    // 4. Hard reload
    console.log('🔄 Performing hard reload...');
    window.location.reload();
  } catch (error) {
    console.error('Error clearing caches:', error);
    // Still try to reload even if cache clearing fails
    window.location.reload();
  }
};
